import 'swiper/css';
import 'swiper/css/a11y';

import styles from './BusinessResourceGrid.module.scss';

import React, { FC } from 'react';

import { InternalLinkV2 } from 'components/ContentLinkV2/InternalLinkV2';
import { Grid } from 'components/Grid/Grid';
import { Image } from 'components/Image/Image';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { TextV2 } from 'components/TextV2/TextV2';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Subject } from 'api/contentful/contentTypes.cloverdotcom';
import { FlatEntry } from 'services/cms/flattenContentful';
import { AppType } from 'services/cms/typesUtil';

type BusinessResourceGridProps = AppType.Component<
  FlatEntry<{
    headline?: string;
    items: FlatEntry<Subject>[];
    headingTagName?: keyof React.ReactHTML;
  }>
>;

export const BusinessResourceGrid: FC<BusinessResourceGridProps> = ({
  _attributes,
  headline,
  items = [],
  headingTagName = 'h2',
  ...props
}) => {
  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;
  const swiperProps = {
    speed: 500,
    loop: true,
    slidesPerView: 'auto',
    slidesPerGroup: 1,
    spaceBetween: 16,
  };
  const cardList = items.map(item => businessGridCard(item));

  return (
    <Grid container className={styles.container} justifyContent="center" {...props} {..._attributes}>
      <Grid item xs={12} md={10} lg={8}>
        {headline && (
          <TextV2
            hasId
            variant="h2-bold"
            tagName={headingTagName}
            className={styles.containerTitle}
            value={headline}
          />
        )}
      </Grid>
      {!!items.length && (
        <Grid container>
          {isMobile && (
            <Swiper {...swiperProps} className={styles.containerProductSlider}>
              {cardList.map(card => (
                <SwiperSlide key={card.key}>{card}</SwiperSlide>
              ))}
            </Swiper>
          )}
          {!isMobile && cardList}
        </Grid>
      )}
    </Grid>
  );
};

const businessGridCard = ({ id, image, title, blurb, link, richBlurb }: FlatEntry<Subject>) => (
  <Grid item xs={12} lg={6} key={id} className={styles.containerCardGridStyle}>
    <div className={styles.containerCard}>
      {image?.file?.url && (
        <div>
          <Image src={image?.file?.url} className={styles.containerCardImage} />
        </div>
      )}
      <div className={styles.containerCardDetails}>
        <TextV2
          hasId
          variant="h5-medium"
          tagName="h5"
          value={title}
          className={styles.containerCardDetailsTitle}
        />
        <TextV2 variant="body" tagName="div" value={blurb} />
        <RichBlurb document={richBlurb} />
        <InternalLinkV2 entry={link} className={styles.containerCardDetailsLink} />
      </div>
    </div>
  </Grid>
);

BusinessResourceGrid.displayName = 'BusinessResourceGrid';
